/**
 * GA4 e-commerce data layer helpers.
 *
 * All push functions reset `ecommerce: null` first (GA4 best practice — avoids
 * concatenating items across events). Safe no-op if `window.dataLayer` is absent
 * (e.g. analytics disabled or before GTM is initialized).
 */

const CURRENCY = "EUR";

const money = (v) => {
  const n = Number(v) || 0;
  return Math.round(n * 100) / 100;
};

/**
 * Push a generic GA4 ecommerce event.
 * @param {string} name - e.g. 'view_item_list', 'add_to_cart', 'begin_checkout'
 * @param {Array<object>} items - GA4 item objects
 * @param {number} [value] - total monetary value; computed from items if omitted
 * @param {object} [extra] - extra ecommerce fields merged in (e.g. {item_list_name})
 */
export const pushEvent = (name, items, value, extra = {}) => {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.dataLayer)) return;

  const cleanItems = (items || []).filter(Boolean);
  const computedValue =
    value !== undefined && value !== null
      ? value
      : cleanItems.reduce(
          (acc, it) => acc + (Number(it.price) || 0) * (Number(it.quantity) || 1),
          0,
        );

  const ecommerce = {
    currency: CURRENCY,
    value: money(computedValue),
    items: cleanItems,
    ...extra,
  };

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event: name, ecommerce });
};

/**
 * GA4 `purchase` event — same shape as pushEvent + transaction_id.
 * @param {string} transactionId - order ID / reservation UUID
 * @param {Array<object>} items
 * @param {number} value - final paid amount
 * @param {object} [extra] - e.g. {tax, shipping, coupon}
 */
export const pushPurchase = (transactionId, items, value, extra = {}) => {
  pushEvent("purchase", items, value, {
    transaction_id: String(transactionId),
    ...extra,
  });
};

/**
 * Push a non-ecommerce event (no ecommerce payload).
 * Used for one-off events like waiting_list_submit.
 */
export const pushCustomEvent = (name, data = {}) => {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.dataLayer)) return;
  window.dataLayer.push({ event: name, ...data });
};

// ─── Cart → GA4 items builders ──────────────────────────────────────────────

/**
 * Build GA4 items from the Redux `bookingParam.rooms[]` for hotel flow.
 * Each room becomes one item; options become additional items.
 */
export const hotelCartItems = (rooms, nights) => {
  const items = [];
  if (!Array.isArray(rooms)) return items;

  const n = Math.max(1, Number(nights) || 1);

  rooms.forEach((room, idx) => {
    if (!room || !room.room_type) return;
    const total = Number(room.room_total_price) || 0;
    const unit = total > 0 && n > 0 ? total / n : Number(room.night_price) || 0;
    items.push({
      item_id: String(room.room_type),
      item_name: String(room.name || room.room_type),
      item_category: "hotel_room",
      item_variant: `room_${idx + 1}`,
      price: money(unit),
      quantity: n,
    });

    (room.options || []).forEach((opt) => {
      if (!opt || !opt.code) return;
      items.push({
        item_id: `option_${opt.code}`,
        item_name: String(opt.label || opt.name || opt.code),
        item_category: "hotel_option",
        price: money(Number(opt.price) || 0),
        quantity: Number(opt.quantity) || 1,
      });
    });
  });

  return items;
};

/**
 * Sum of room nights + option totals from the rooms array.
 */
export const hotelCartValue = (rooms, nights) => {
  if (!Array.isArray(rooms)) return 0;
  return rooms.reduce((acc, room) => {
    if (!room) return acc;
    const roomTotal = Number(room.room_total_price) || 0;
    const optsTotal = (room.options || []).reduce(
      (o, opt) =>
        o + (Number(opt && opt.price) || 0) * (Number(opt && opt.quantity) || 1),
      0,
    );
    return acc + roomTotal + optsTotal;
  }, 0);
};

/**
 * Build a single GA4 item for a room being viewed/selected in the catalog.
 * Used by view_item_list / select_item before the room is committed to the cart.
 */
export const roomCatalogItem = (room, listName = "rooms_catalog", index = 0) => {
  if (!room) return null;
  return {
    item_id: String(room.code || room.id || ""),
    item_name: String(room.name || room.code || ""),
    item_category: "hotel_room",
    item_list_name: listName,
    index,
    price: money(Number(room.total_price || room.price || 0)),
    quantity: 1,
  };
};

/**
 * Build a single GA4 item for an addon/option in the catalog.
 */
export const optionCatalogItem = (option, listName = "options_catalog", index = 0) => {
  if (!option) return null;
  return {
    item_id: `option_${option.code || option.id || ""}`,
    item_name: String(option.label || option.name || option.code || ""),
    item_category: "hotel_option",
    item_list_name: listName,
    index,
    price: money(Number(option.price) || 0),
    quantity: 1,
  };
};

// ─── Restaurant flow builders ───────────────────────────────────────────────

/**
 * Build a single GA4 item for a restaurant booking line.
 * Input shape matches both restaurantSlice and entries of
 * restaurantHotelSlice.restaurantBooking[].
 */
const restaurantBookingItem = (booking, depositPerCover = 0) => {
  if (!booking) return null;
  const adults = Number(booking.adults) || 1;
  return {
    item_id: String(booking.code || "restaurant_booking"),
    item_name: String(booking.menu || booking.code || "Restaurant"),
    item_category: "restaurant_booking",
    item_variant: booking.service === "M" ? "lunch" : "dinner",
    price: money(depositPerCover),
    quantity: adults,
  };
};

/**
 * Build GA4 items from restaurant state.
 *
 * Accepts either:
 *  - the `restaurant` slice (single booking: {date, service, hours, adults, menu, code})
 *  - the `restaurantHotel` slice ({restaurantBooking: [...]})
 *
 * `depositPerCover` is optional; pass `hotelSettings.secure_restaurant_amount`
 * when available so the item price reflects the actual deposit.
 */
export const restaurantItemsFromState = (state, depositPerCover = 0) => {
  if (!state) return [];
  const items = [];

  if (Array.isArray(state.restaurantBooking)) {
    state.restaurantBooking.forEach((b) => {
      const it = restaurantBookingItem(b, depositPerCover);
      if (it) items.push(it);
    });
    return items;
  }

  if (state.date || state.menu || state.code) {
    const it = restaurantBookingItem(state, depositPerCover);
    if (it) items.push(it);
  }

  return items;
};

/**
 * Build catalog items for the restaurant menus list (view_item_list).
 */
export const restaurantMenuCatalogItem = (menu, service, index = 0) => {
  if (!menu) return null;
  return {
    item_id: String(menu.code || menu.id || ""),
    item_name: String(menu.name || menu.code || ""),
    item_category: "restaurant_booking",
    item_variant: service === "M" ? "lunch" : "dinner",
    item_list_name: service === "M" ? "menus_lunch" : "menus_dinner",
    index,
    price: 0,
    quantity: 1,
  };
};
